import React from 'react';
import { Badge, Box, SpaceBetween, Popover } from '@cloudscape-design/components';
import { UNMAPPED, categoryLabel } from '../utils/rubiconServiceCategories';

/**
 * Rubicon service-category badge.
 *
 * A thin wrapper over Cloudscape's Badge whose only job is to keep the
 * label/colour pairing in ONE place, so the General tab and the Modernization tab
 * can never drift apart.
 *
 *   Category 1 (Foundation)              grey    not modern
 *   Category 2 (Managed)                 blue    modern
 *   Category 3 (Differentiated/Native)   green   modern
 *   Unmapped                             red     not modern; ARR still counted
 *
 * All four are native Badge colours, so no custom styling is needed. (Badge in
 * @cloudscape-design/components v3 accepts only `color` and `children` — there is
 * no `style` prop — so a colour outside that set would require a custom element.)
 */
export const COLOR_BY_CATEGORY = {
  1: 'grey',
  2: 'blue',
  3: 'green',
  [UNMAPPED]: 'red',
};

/**
 * Hex equivalents of the badge colours, for the pie chart on the Modernization
 * tab. Kept beside COLOR_BY_CATEGORY on purpose: a chart whose colours disagree
 * with the table's badges is worse than no chart. Values are Cloudscape's status
 * palette (grey / blue / green / red).
 */
export const CHART_COLOR_BY_CATEGORY = {
  1: '#8c8c8c',
  2: '#0972d3',
  3: '#037f0c',
  [UNMAPPED]: '#d91515',
};

const CategoryBadge = ({ category, arr, formatCurrency }) => {
  const color = COLOR_BY_CATEGORY[category] || 'red';
  const label = categoryLabel(category);

  if (category !== UNMAPPED) {
    return <Badge color={color}>{label}</Badge>;
  }

  // Unmapped: show the ARR the service accounts for, so a reviewer can see how
  // much spend is unclassified without leaving the table. The ARR still counts in
  // total ARR — it is simply never counted as modern.
  const amount =
    arr != null && formatCurrency ? formatCurrency(arr) : arr != null ? String(arr) : null;

  return (
    <SpaceBetween direction="horizontal" size="xs">
      <Popover
        dismissButton={false}
        position="top"
        size="medium"
        triggerType="custom"
        content={
          <Box>
            This service code is not in Rubicon&apos;s category mapping. Its ARR is still
            counted in total ARR, but it is never counted as modern — so it lowers the
            modernization index until it is classified.
          </Box>
        }
      >
        <Badge color={color}>{label}</Badge>
      </Popover>
      {amount && (
        <Box variant="small" color="text-status-inactive">
          {amount}
        </Box>
      )}
    </SpaceBetween>
  );
};

export default CategoryBadge;
